import { Modal } from '@/shared/ui/Modal/Modal';
import { GeneralSelectedI } from '@/shared/model/types/generalSelected';
import s from './DeleteAccountModal.module.scss';
import { Button } from '@/shared/ui/Button/Button';
import { Input } from '@/shared/ui/Input/Input';
import { useDeleteAccountMutation, useHardDeleteAccountMutation } from '@/features/DeleteAccount';
import { useDeleteClubMutation } from '@/entities/Club';
import { useEffect, useState } from 'react';
import { useError } from '@/shared/lib/hooks/useError';
import { useAlert } from '@/shared/lib/hooks/useAlert';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';
import { hasNonActiveSelection } from '@/shared/lib/utils/accountPresentation';

interface UnbindingHWIDModalProps extends GeneralSelectedI {
  onClose: () => void;
  open: boolean;
  resetSelectedRows?: () => void;
  mode?: 'account' | 'club';
  deletionType?: 'lifecycle' | 'hard';
}

export const DeleteAccountModal = (props: UnbindingHWIDModalProps) => {
  const {
    open,
    onClose,
    arrayOfName,
    arrayOfId,
    accountStatuses,
    resetSelectedRows,
    mode = 'account',
    deletionType = 'lifecycle',
  } = props;
  const onAlert = useAlert();
  const { isMobile } = useScreenDetector();
  const isHardDelete = mode === 'account' && deletionType === 'hard';
  const [triggerDelete, { error: accountError, isLoading: isAccountDeleting }] = useDeleteAccountMutation();
  const [triggerHardDelete, { error: hardDeleteError, isLoading: isHardDeleteLoading }] =
    useHardDeleteAccountMutation();
  const [triggerDeleteClub, { error: clubError, isLoading: isClubDeleting }] = useDeleteClubMutation();
  const [reason, setReason] = useState('');
  const error = isHardDelete ? hardDeleteError : (accountError ?? clubError);
  const isLoading = isHardDeleteLoading || isAccountDeleting || isClubDeleting;

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const onDelete = async () => {
    const normalizedReason = reason.trim();
    if ((!isHardDelete && !normalizedReason) || isLoading || hasNonActiveSelection(accountStatuses)) return;

    try {
      if (mode === 'club') {
        for (const id of arrayOfId) {
          await triggerDeleteClub({ id, reason: normalizedReason }).unwrap();
        }
      } else {
        if (isHardDelete) {
          await triggerHardDelete({ ids: arrayOfId }).unwrap();
        } else {
          await triggerDelete({ ids: arrayOfId, reason: normalizedReason }).unwrap();
        }
      }
      resetSelectedRows && resetSelectedRows();
      onAlert({
        header:
          arrayOfName.length === 1
            ? mode === 'club'
              ? 'Клуб'
              : 'Учётная запись'
            : mode === 'club'
              ? 'Клубы:'
              : 'Учётные записи пользователей:',
        body: arrayOfName,
        footer: isHardDelete
          ? arrayOfName.length === 1
            ? 'удалена'
            : 'удалены'
          : arrayOfName.length === 1
            ? 'поставлена на удаление'
            : 'поставлены на удаление',
      });
      onClose();
    } catch {
      return;
    }
  };

  useError(error);

  return (
    <Modal
      onClose={onClose}
      open={open}
      title={
        mode === 'club' ? 'Удаление клуба' : isHardDelete ? 'Полное удаление учётной записи' : 'Удаление учётной записи'
      }
      titleSize={'h1'}
    >
      <div className={s.wrapper}>
        {arrayOfName.length === 1 ? (
          <span className={s.text}>
            Вы действительно хотите удалить {mode === 'club' ? 'клуб' : 'учётную запись'}{' '}
            <span className={s.name}>{arrayOfName[0] || '—'}</span>?
          </span>
        ) : (
          <span className={s.text}>
            Вы действительно хотите удалить {mode === 'club' ? 'клубы' : 'учётные записи пользователей'}:{' '}
            {arrayOfName.map((name, index) => {
              if (index === 9) return '... ';
              if (index > 9) return null;
              return (
                <span key={index} className={s.name}>
                  {name || '—'}
                  {index < arrayOfName.length - 1 ? ', ' : ''}
                </span>
              );
            })}
            ?
          </span>
        )}
        <span className={s.description}>
          {isHardDelete
            ? 'После подтверждения аккаунт и связанные с ним данные будут удалены без возможности восстановления.'
            : `После подтверждения ${mode === 'club' ? 'клуб' : 'аккаунт'} будет недоступен.${
                mode === 'account' ? ' Восстановление возможно в течение 30 календарных дней.' : ''
              }`}
        </span>
        {!isHardDelete && (
          <div className={s.reasonInput}>
            <Input
              label="Причина удаления"
              value={reason}
              onChange={(e) => setReason(typeof e === 'string' ? e : e.target.value)}
              error={!reason.trim() ? 'Причина удаления обязательна' : ''}
              placeholder="Укажите причину удаления"
              baseAddon={false}
            />
          </div>
        )}
      </div>
      <div className={s.actions}>
        <Button
          color={'red'}
          onClick={() => void onDelete()}
          disabled={isLoading || (!isHardDelete && !reason.trim())}
          size={isMobile ? 's' : 'm'}
        >
          {isHardDelete ? 'Удалить навсегда' : 'Удалить'}
        </Button>
        <Button color={'red'} variant={'outline'} onClick={onClose} size={isMobile ? 's' : 'm'}>
          Отменить
        </Button>
      </div>
    </Modal>
  );
};
